import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/api/client';
import { Input } from '@/components/atoms/Input';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Textarea } from '@/components/atoms/Textarea';
import { useAuthStore } from '@/store/authStore';
import { useScrollLock } from '@/hooks/useScrollLock';

interface AdminPost {
  id: number;
  board_id: number;
  board_name: string | null;
  title: string;
  user_id: string;
  author_nickname: string | null;
  is_blinded: boolean;
  blinded_reason: string | null;
  is_deleted: boolean;
  view_count: number;
  like_count: number;
  comment_count: number;
  created_at: string;
}

const LIMIT = 30;

function fmt(iso: string) {
  return new Date(iso).toISOString().slice(0, 16).replace('T', ' ');
}

interface BoardOption {
  id: number;
  slug: string;
  name: string;
}

export function PostsListPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const hidden = params.get('hidden') ?? '';
  const boardId = params.get('boardId') ?? '';
  const page = Number(params.get('page') ?? 1);
  const [draftQ, setDraftQ] = useState(q);
  const me = useAuthStore((s) => s.me);
  const isSuper = me?.adminRole === 'super';

  const qc = useQueryClient();
  // 게시판 드롭다운 옵션 — 어드민 보드 전체(비활성/숨김 포함)
  const boardsQuery = useQuery({
    queryKey: ['admin', 'boards', 'options'],
    queryFn: () => get<{ boards: BoardOption[] }>('/admin/boards'),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'posts', { q, hidden, boardId, page }],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (q) sp.set('q', q);
      if (hidden) sp.set('hidden', hidden);
      if (boardId) sp.set('board_id', boardId);
      sp.set('page', String(page));
      sp.set('limit', String(LIMIT));
      return get<{ posts: AdminPost[]; total: number }>(`/admin/posts?${sp}`);
    },
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;

  const [actionTarget, setActionTarget] = useState<{ type: 'hide' | 'delete'; post: AdminPost } | null>(
    null,
  );
  const [reason, setReason] = useState('');
  useScrollLock(actionTarget !== null);

  const refetch = () => qc.invalidateQueries({ queryKey: ['admin', 'posts'] });

  const hideMut = useMutation({
    mutationFn: (postId: number) => post(`/admin/posts/${postId}/hide`, { reason }),
    onSuccess: () => {
      setActionTarget(null);
      setReason('');
      refetch();
    },
  });
  const unhideMut = useMutation({
    mutationFn: (postId: number) => post(`/admin/posts/${postId}/unhide`),
    onSuccess: refetch,
  });
  const deleteMut = useMutation({
    mutationFn: (postId: number) => post(`/admin/posts/${postId}/delete`, { reason }),
    onSuccess: () => {
      setActionTarget(null);
      setReason('');
      refetch();
    },
  });

  function applySearch() {
    const next = new URLSearchParams(params);
    if (draftQ) next.set('q', draftQ);
    else next.delete('q');
    next.set('page', '1');
    setParams(next);
  }
  function setHidden(value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set('hidden', value);
    else next.delete('hidden');
    next.set('page', '1');
    setParams(next);
  }
  function setBoardFilter(value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set('boardId', value);
    else next.delete('boardId');
    next.set('page', '1');
    setParams(next);
  }
  function goPage(p: number) {
    const next = new URLSearchParams(params);
    next.set('page', String(p));
    setParams(next);
  }

  return (
    <div className="w-full">
      <h1 className="mb-6 text-2xl font-extrabold">게시글 관리</h1>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          placeholder="제목 검색"
          value={draftQ}
          onChange={(e) => setDraftQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applySearch()}
          className="max-w-xs"
        />
        <Button size="sm" onClick={applySearch}>
          검색
        </Button>
        <select
          value={boardId}
          onChange={(e) => setBoardFilter(e.target.value)}
          className="h-9 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-sm"
          aria-label="게시판 필터"
        >
          <option value="">전체 게시판</option>
          {boardsQuery.data?.boards.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <select
          value={hidden}
          onChange={(e) => setHidden(e.target.value)}
          className="h-9 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-sm"
        >
          <option value="">전체 상태</option>
          <option value="false">정상만</option>
          <option value="true">블라인드만</option>
        </select>
        <div className="ml-auto text-xs text-[var(--color-text-sub)]">총 {data?.total ?? 0}건</div>
      </div>

      {isLoading ? (
        <Skeleton className="h-80" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg)] text-left">
              <tr>
                <th className="p-3">ID</th>
                <th className="p-3">게시판</th>
                <th className="p-3">제목</th>
                <th className="p-3">작성자</th>
                <th className="p-3">상태</th>
                <th className="p-3">조회</th>
                <th className="p-3">댓글</th>
                <th className="p-3">작성일</th>
                <th className="p-3 text-right">액션</th>
              </tr>
            </thead>
            <tbody>
              {data?.posts.map((p) => (
                <tr key={p.id} className="border-t border-[var(--color-border)]">
                  <td className="p-3 text-xs text-[var(--color-text-sub)]">{p.id}</td>
                  <td className="p-3 text-xs">{p.board_name ?? '-'}</td>
                  <td className="p-3 max-w-md truncate">
                    <Link
                      to={`/post/${p.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      {p.title}
                    </Link>
                  </td>
                  <td className="p-3">
                    <Link
                      to={`/admin/users/${p.user_id}`}
                      className="text-xs text-[var(--color-primary)] hover:underline"
                    >
                      {p.author_nickname ?? '-'}
                    </Link>
                  </td>
                  <td className="p-3">
                    {p.is_deleted ? (
                      <Badge variant="default">삭제</Badge>
                    ) : p.is_blinded ? (
                      <Badge variant="fall">블라인드</Badge>
                    ) : (
                      <Badge variant="rise">정상</Badge>
                    )}
                  </td>
                  <td className="p-3 text-xs">{p.view_count}</td>
                  <td className="p-3 text-xs">{p.comment_count}</td>
                  <td className="p-3 text-xs text-[var(--color-text-sub)]">{fmt(p.created_at)}</td>
                  <td className="p-3 text-right">
                    {p.is_blinded ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={unhideMut.isPending}
                        onClick={() => unhideMut.mutate(p.id)}
                      >
                        해제
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setActionTarget({ type: 'hide', post: p })}
                      >
                        숨김
                      </Button>
                    )}
                    {isSuper && !p.is_deleted && (
                      <Button
                        size="sm"
                        variant="danger"
                        className="ml-1"
                        onClick={() => setActionTarget({ type: 'delete', post: p })}
                      >
                        완전삭제
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {data?.posts.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-[var(--color-text-sub)]">
                    데이터가 없어요.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > LIMIT && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => goPage(page - 1)}>
            이전
          </Button>
          <span className="text-sm text-[var(--color-text-sub)]">
            {page} / {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => goPage(page + 1)}
          >
            다음
          </Button>
        </div>
      )}

      {actionTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setActionTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-[var(--color-surface)] p-6"
          >
            <h3 className="mb-3 text-lg font-bold">
              {actionTarget.type === 'hide' ? '게시글 숨김' : '게시글 완전삭제'}
            </h3>
            <p className="mb-3 truncate text-sm text-[var(--color-text-sub)]">
              {actionTarget.post.title}
            </p>
            {actionTarget.type === 'delete' && (
              <p className="mb-3 rounded-md bg-[var(--color-warn-bg)] p-2 text-xs text-[var(--color-warn)]">
                ⚠️ 완전삭제는 복구 불가. admin_logs 에 백업이 보관됩니다.
              </p>
            )}
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="사유 (필수, 2자 이상)"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm"
            />
            <div className="mt-4 flex gap-2">
              <Button variant="ghost" fullWidth onClick={() => setActionTarget(null)}>
                취소
              </Button>
              <Button
                fullWidth
                variant="danger"
                disabled={reason.trim().length < 2}
                loading={hideMut.isPending || deleteMut.isPending}
                onClick={() =>
                  actionTarget.type === 'hide'
                    ? hideMut.mutate(actionTarget.post.id)
                    : deleteMut.mutate(actionTarget.post.id)
                }
              >
                적용
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
